import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  PLATFORM_ID,
  viewChild,
} from '@angular/core';
import type { EChartsOption } from 'echarts';
import {
  BarChart,
  HeatmapChart,
  LineChart,
  MapChart,
  PieChart,
  ScatterChart,
} from 'echarts/charts';
import {
  DataZoomComponent,
  GeoComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import type { EChartsType, Payload } from 'echarts/core';
import { init, use } from 'echarts/core';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import type { ECElementEvent } from 'echarts/types/dist/shared';

import { EmptyEchartsComponent } from './empty-echarts/empty-echarts.component';
import {
  CUSTOMER_PORTAL_ECHARTS_THEME,
  registerCustomerPortalEchartsTheme,
} from './echarts-theme';

use([
  BarChart,
  HeatmapChart,
  LineChart,
  MapChart,
  PieChart,
  ScatterChart,
  GraphicComponent,
  GridComponent,
  DataZoomComponent,
  GeoComponent,
  LegendComponent,
  LabelLayout,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

@Component({
  selector: 'shared-echarts',
  imports: [EmptyEchartsComponent],
  templateUrl: './echarts.component.html',
  styleUrl: './echarts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EchartsComponent implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private chartInstance?: EChartsType;
  private resizeObserver?: ResizeObserver;
  private resizeAnimationFrame?: number;
  private viewInitialized = false;
  private destroyed = false;
  private documentPointerDownHandler?: (event: PointerEvent) => void;

  private readonly _ = effect(() => {
    this.option();
    this.render();
  });

  readonly option = input.required<EChartsOption>();
  readonly accessibleName = input<string | null>(null);
  readonly hasBorderIfNoData = input(true);
  readonly hasData = computed(() => this.optionHasData(this.option()));
  readonly chartClick = output<ECElementEvent>();
  readonly chartHover = output<{ index: number; datasetIndex: number }>();
  readonly chartContextMenu = output<ECElementEvent>();
  readonly chartElement =
    viewChild.required<ElementRef<HTMLDivElement>>('chart');

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || this.chartInstance) {
      return;
    }

    registerCustomerPortalEchartsTheme();
    this.chartInstance = init(
      this.chartElement().nativeElement,
      CUSTOMER_PORTAL_ECHARTS_THEME,
    );
    this.chartInstance.on('click', (event: ECElementEvent) =>
      this.chartClick.emit(event),
    );
    this.chartInstance.on('mouseover', (event: ECElementEvent) =>
      this.chartHover.emit({
        index: event.dataIndex ?? 0,
        datasetIndex: event.seriesIndex ?? 0,
      }),
    );
    this.chartInstance.on('contextmenu', (event: ECElementEvent) =>
      this.chartContextMenu.emit(event),
    );

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() =>
        this.chartInstance?.resize(),
      );
      this.resizeObserver.observe(this.chartElement().nativeElement);
    }
    this.resizeAnimationFrame = requestAnimationFrame(() => {
      this.resizeAnimationFrame = undefined;
      this.chartInstance?.resize();
    });
    this.documentPointerDownHandler = (event) =>
      this.onDocumentPointerDown(event);
    document.addEventListener(
      'pointerdown',
      this.documentPointerDownHandler,
      true,
    );

    this.viewInitialized = true;
    this.render();
  }

  ngOnDestroy(): void {
    this.destroyed = true;

    if (this.resizeAnimationFrame !== undefined) {
      cancelAnimationFrame(this.resizeAnimationFrame);
      this.resizeAnimationFrame = undefined;
    }

    if (this.documentPointerDownHandler) {
      document.removeEventListener(
        'pointerdown',
        this.documentPointerDownHandler,
        true,
      );
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.chartInstance?.dispose();
    this.chartInstance = undefined;
    this.viewInitialized = false;
  }

  resize(): void {
    if (this.destroyed) return;

    this.chartInstance?.resize();
  }

  dispatchAction(action: Payload): void {
    if (this.destroyed) return;

    this.chartInstance?.dispatchAction(action);
  }

  private hideTooltip(): void {
    this.chartInstance?.dispatchAction({ type: 'hideTip' });
  }

  private onDocumentPointerDown(event: PointerEvent): void {
    const { target } = event;

    if (!(target instanceof HTMLElement)) {
      this.hideTooltip();

      return;
    }

    if (
      this.chartElement().nativeElement.contains(target) ||
      target.closest('[data-echarts-tooltip-action]')
    ) {
      return;
    }

    this.hideTooltip();
  }

  private render(): void {
    if (!this.viewInitialized || !this.chartInstance) {
      return;
    }

    this.chartInstance.setOption(this.option(), { notMerge: true });
  }

  private optionHasData(option: EChartsOption): boolean {
    const { series } = option;
    let seriesList: unknown[] = [];

    if (Array.isArray(series)) {
      seriesList = series;
    } else if (series) {
      seriesList = [series];
    }

    return seriesList.some((item) => {
      if (!item || typeof item !== 'object' || !('data' in item)) {
        return false;
      }

      const { data } = item;

      return Array.isArray(data) ? data.length > 0 : data !== undefined;
    });
  }
}
